import { useCallback, useEffect, useRef } from 'react'

import type { ChimeSound } from './arrival-chime'

/**
 * The one thing that turns a `ChimeSound` into noise - §9.675.
 *
 * <p><b>One player for every sound</b>: the arrival chime, the ready bell and a picker's preview all come through
 * here. It used to live inside `useArrivalChime` with the waveform and the loudness written into it, which is the
 * shape that would have played a sine bell as a square buzz the day a second sound arrived.
 */

/** The floor an exponential ramp starts from and falls to - it cannot reach nought. */
const SILENCE = 0.0001

/** How long after a note's end its oscillator is stopped, so the fall is heard rather than cut. */
const RELEASE_SECONDS = 0.02

/**
 * Schedules every note of `sound` on `context`, starting now.
 *
 * <p><b>Ramped rather than switched</b>: an abrupt start or stop clicks, and a click on every note is what makes a
 * notification sound cheap. The rise is exponential from {@link SILENCE} to the sound's own `peak` over its own
 * `attack`, and the fall is back to {@link SILENCE} by the note's end.
 */
export function playChimeSound(context: AudioContext, sound: ChimeSound): void {
  for (const tone of sound.tones) {
    const start = context.currentTime + tone.at
    const oscillator = context.createOscillator()
    const gain = context.createGain()
    oscillator.connect(gain)
    gain.connect(context.destination)
    oscillator.type = sound.wave
    oscillator.frequency.value = tone.hertz
    gain.gain.setValueAtTime(SILENCE, start)
    gain.gain.exponentialRampToValueAtTime(sound.peak, start + sound.attack)
    gain.gain.exponentialRampToValueAtTime(SILENCE, start + tone.duration)
    oscillator.start(start)
    oscillator.stop(start + tone.duration + RELEASE_SECONDS)
  }
}

/**
 * A function that plays a sound on this component's own `AudioContext` - §9.675.
 *
 * <p><b>Created on the first sound and not before.</b> Constructing one on mount would leave a suspended context on
 * every board in the building, and a browser will not start one without a gesture - so the first press of a switch
 * or a preview is what lets it run. Closed when the component unmounts.
 *
 * <p><b>Failure is silent, deliberately.</b> Blocked, unavailable or no output: the screen still tells the story,
 * and a screen that threw because it could not beep would be worse than one that did not beep.
 */
export function useChimeOutput(): (sound: ChimeSound) => void {
  const contextRef = useRef<AudioContext | null>(null)

  useEffect(
    () => () => {
      void contextRef.current?.close()
      contextRef.current = null
    },
    [],
  )

  return useCallback((sound: ChimeSound) => {
    try {
      const Ctor =
        window.AudioContext ??
        (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
      if (!Ctor) return
      contextRef.current ??= new Ctor()
      const context = contextRef.current
      void context.resume()
      playChimeSound(context, sound)
    } catch {
      // Blocked, unavailable, or no output. The screen still tells the story.
    }
  }, [])
}
